import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useLinkWithParams } from "@/hooks/useNavigateKeepParams";
import { useProviderPermissions } from "@/hooks/useProviderPermissions";
import kubrickLogo from "@/assets/kubrick-logo.png";

const pipelineLink = {
  to: "/pipeline",
  labelKey: "nav.pipeline",
} as const;

const navLinks = [
  { to: "/", labelKey: "nav.home" },
  pipelineLink,
  {
    to: "/schema-builder",
    labelKey: "nav.schema-builder",
  },
] as const;

/** Always-visible Providers link under the Provider Management dropdown. */
const providersListLink = {
  to: "/provider-management/providers",
  labelKey: "nav.providers",
} as const;

/** Known Mappings lives under Provider Management (not top-level nav). */
const knownMappingsLink = {
  to: "/known-mappings",
  labelKey: "nav.knownMappings",
} as const;

/** Admin-only global Ingestion History under Provider Management. */
const ingestionHistoryLink = {
  to: "/provider-management/ingestion-history",
  labelKey: "nav.ingestionHistory",
} as const;

/** Admin-only create link — prepended when canCreateProvider is true. */
const addNewProviderLink = {
  to: "/provider-management/providers/new",
  labelKey: "nav.addNewProvider",
} as const;

export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useTelemetry();
  const { canCreateProvider, canViewGlobalKnownMappings, canViewGlobalIngestionHistory } =
    useProviderPermissions();
  const links = [...navLinks];
  const linkTo = useLinkWithParams();
  const providerManagementLinks = [
    ...(canCreateProvider ? [addNewProviderLink] : []),
    providersListLink,
    ...(canViewGlobalIngestionHistory ? [ingestionHistoryLink] : []),
    ...(canViewGlobalKnownMappings ? [knownMappingsLink] : []),
  ];

  const handleNavClick = (to: string, label: string) => {
    track("navigation_clicked", {
      destination: to,
      label,
      previousPage: location.pathname,
    });
  };

  return (
    <header className="sticky top-0 z-40 w-full" style={{ backgroundColor: "var(--color-nav-bg)" }}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link
          to={linkTo("/")}
          className="flex items-center gap-3"
          aria-label="Home"
          style={{ color: "var(--color-nav-text-active)" }}
          onClick={() => handleNavClick("/", "Home")}
        >
          <img src={kubrickLogo} alt="Kubrick" className="h-7 w-auto" />
          <span
            className="hidden sm:inline-block text-[0.65rem] font-medium tracking-normal opacity-60"
            style={{ color: "var(--color-nav-text)" }}
          >
            Intelligent Data Intake
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((link) => {
            const isActive = location.pathname === link.to;
            const label = t(link.labelKey);

            return (
              <Link
                key={link.to}
                to={linkTo(link.to)}
                onClick={() => handleNavClick(link.to, label)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  isActive
                    ? "text-white bg-white/10"
                    : "text-[var(--color-nav-text)] hover:text-white hover:bg-white/[0.06]"
                )}
              >
                {label}
                <span
                  className={cn(
                    "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[var(--color-brand-primary)] transition-all duration-300",
                    isActive ? "w-6 opacity-100" : "w-0 opacity-0"
                  )}
                />
              </Link>
            );
          })}

          {/* Provider Management sub-menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={cn(
                  "relative flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  location.pathname.startsWith("/provider-management")
                    ? "text-white bg-white/10"
                    : "text-[var(--color-nav-text)] hover:text-white hover:bg-white/[0.06]"
                )}
                aria-label={t("nav.providerManagement")}
              >
                {t("nav.providerManagement")}
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                <span
                  className={cn(
                    "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[var(--color-brand-primary)] transition-all duration-300",
                    location.pathname.startsWith("/provider-management")
                      ? "w-6 opacity-100"
                      : "w-0 opacity-0"
                  )}
                />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[200px] rounded-lg border border-[var(--color-border)] bg-white p-1.5 shadow-lg animate-in fade-in-0 zoom-in-95"
                sideOffset={8}
                align="start"
              >
                {providerManagementLinks.map((link) => (
                  <DropdownMenu.Item
                    key={link.to}
                    className={cn(
                      "flex items-center rounded-md px-2 py-1.5 text-sm cursor-pointer outline-none",
                      location.pathname === link.to
                        ? "bg-[var(--color-brand-surface)] text-[var(--color-brand-primary-dark)] font-medium"
                        : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
                    )}
                    onSelect={() => {
                      track("navigation_clicked", {
                        destination: link.to,
                        label: t(link.labelKey),
                        previousPage: location.pathname,
                      });
                      navigate(linkTo(link.to));
                    }}
                  >
                    {t(link.labelKey)}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </nav>
      </div>
    </header>
  );
}
